import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as routes from "../../../routes";
import {
  GenericTable,
  Spinner,
  Pagination,
  SearchableDropdown,
  Tooltip,
} from "../../../Components/Common";
import {
  LEDGER_COLUMNS,
  LEDGER_LABELS,
  MISC,
  TRANSACTION_TYPE_LEDGER,
} from "../Ledgers/constants";
import { useLedger } from "../Hooks/useLedger";
import { FaUser, FaLongArrowAltUp, FaLongArrowAltDown, } from "react-icons/fa";
import { formatDateDDMMYYYY, toProperCase } from "../../../Utils";
import { SquareArrowOutUpRight } from "lucide-react";
import { FiCheck, FiInfo } from "react-icons/fi";

//TODO: By default first user should be selected, and it's ledger should be shown
const UserLedgerList = () => {
  const {
    loading,
    page,
    totalPages,
    rowsPerPage,
    onPageChange,
    onPageSizeChange,
    fetchUserLedger,
    fetchUsers,
  } = useLedger();

  const [selectedUser, setSelectedUser] = useState(null);
  const [ledgerData, setLedgerData] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null,
  });

  const navigate = useNavigate();

  const loadLedger = async (userId) => {
    if (!userId) return;

    const params = { user: userId };

  if (dateRange.from && dateRange.to) {
    params.transaction_date_after = dateRange.from;
    params.transaction_date_before = dateRange.to;
  }

    const data = await fetchUserLedger(params);
    setLedgerData(data || []);
  };

  const handleRowClick = async (row) => {
    if (row.type === TRANSACTION_TYPE_LEDGER.BILL) {
      navigate(routes.VIEW_BILL.replace(":billId", row.linked_to));
    } else if (row.type === TRANSACTION_TYPE_LEDGER.ADVANCE_NOTE) {
      navigate(routes.VIEW_ADVANCE_NOTE.replace(":instanceId", row.linked_to));
    }
  };

  const handleOnSelectDropdown = async (val) => {
  setLedgerData([]);
  if (val) {
    setSelectedUser({ id: val });
  } else {
    setSelectedUser(null);
  }
  };

  useEffect(() => {
    if (selectedUser?.id) {
      loadLedger(selectedUser.id);
    }
  }, [selectedUser, page, rowsPerPage, dateRange]);

  const renderField = (key, row) => {
    switch (key) {
      case "approved_on":
        const date = row.approved_on.split("T")[0];
        return formatDateDDMMYYYY(date);

      case "transaction_amount":
        return (
          <span className="font-medium text-gray-800">
            ₹{row.transaction_amount?.toLocaleString("en-IN")}
          </span>
        );
      case "before_amount":
        return (
          <span className="font-medium text-gray-800">
            ₹{row.before_amount?.toLocaleString("en-IN")}
          </span>
        );

      case "balance":
        return (
          <span
            className={`font-semibold ${
              row.balance >= 0 ? "text-green-500" : "text-red-500"
            }`}
          >
            ₹{row.balance?.toLocaleString("en-IN")}
          </span>
        );

      case "note_type":
        return (
          <span
            className={`px-2 py-0.5 rounded-lg text-xs font-semibold ${
              row.note_type === TRANSACTION_TYPE_LEDGER.BILL
                ? "bg-orange-100 text-orange-700"
                : "bg-emerald-100 text-emerald-700"
            }`}
          >
            {toProperCase(row.note_type)}
          </span>
        );

      case "actions":
        return (
          <button
            className="text-blue-600 cursor-pointer hover:text-blue-800 transition-colors duration-200"
            onClick={() =>
              navigate(
                row.note_type === TRANSACTION_TYPE_LEDGER.BILL
                  ? routes.VIEW_BILL.replace(":billId", row.entity_id)
                  : routes.VIEW_ADVANCE_NOTE.replace(
                      ":instanceId",
                      row.entity_id
                    )
              )
            }
            title="View Details"
          >
            <SquareArrowOutUpRight size={18} />
          </button>
        );  

      default:
        return (
          <span className={row[key] ? "text-gray-800" : "text-gray-400 italic"}>
            {row[key] || "-"}
          </span>
        );
    }
  };

  // Render for mobile
  const renderMobileField = (row) => (
    <div
      key={row.linked_to}
      className="rounded-xl p-3 shadow-sm border border-gray-100 bg-white hover:shadow-md transition-all cursor-pointer mb-2"
      onClick={() =>
        navigate(
          row.note_type === TRANSACTION_TYPE_LEDGER.BILL
            ? routes.VIEW_BILL.replace(":billId", row.entity_id)
            : routes.VIEW_ADVANCE_NOTE.replace(":instanceId", row.entity_id)
        )
      }
    >
      <div className="flex justify-between items-center mb-1">
        <div className="flex items-center gap-1">
          <span className="text-sm font-semibold text-gray-900 truncate">
            {row.site_name || MISC.NOT_AVAILABLE}
          </span>
          <span
            className={`text-[10px] px-2 py-[2px] rounded-md font-medium ${
              row.note_type === TRANSACTION_TYPE_LEDGER.BILL
                ? "bg-orange-100 text-orange-700"
                : "bg-emerald-100 text-emerald-700"
            }`}
          >
            {toProperCase(row.note_type)}
          </span>
        </div>
        <span className="text-[11px] text-gray-500">
          {formatDateDDMMYYYY(row.approved_on.split("T")[0])}
        </span>
      </div>

      <div className="flex justify-between items-center mt-1">
        <span className="text-base font-semibold text-gray-900">
          ₹{row.transaction_amount?.toLocaleString("en-IN")}
        </span>

        <div className="flex items-center gap-1">
          {row.balance < 0 ? (
            <FaLongArrowAltDown size={10} className="text-red-700 text-sm" />
          ) : (
            <FaLongArrowAltUp size={10} className="text-green-700 text-sm" />
          )}
          <span
            className={`text-[12px] font-semibold  ${
              row.balance < 0 ? "text-red-700 " : "text-green-700 "
            }`}
          >
            ₹{Math.abs(row.balance)?.toLocaleString("en-IN")}
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-5">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-2xl font-semibold">User Ledger</h2>
      </div>

      <div className="flex flex-wrap bg-white w-full rounded-2xl shadow-md border px-4 py-4 mb-4 gap-1 md:gap-2 items-end">
        {/* User Dropdown */}
        <div className="flex flex-col w-full md:w-44 mr-4">
          <label className="text-sm font-medium text-gray-700 mb-1">
            Select User
          </label>
          <SearchableDropdown
            fetchFunction={fetchUsers}
            onSelect={handleOnSelectDropdown}
            valueKey="id"
            labelKey="first_name"
            includeAllOption={false}
            placeholder="Select User"
            width="full"
          />
        </div>

        {/* Start Date */}
        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            value={dateRange.from || ""}
            onChange={(e) =>
              setDateRange((prev) => ({ ...prev, from: e.target.value }))
            }
            className="w-36 md:w-40 border-gray-300 rounded-lg px-3 py-2 text-gray-700  disabled:bg-gray-100 disabled:text-gray-400"
            disabled={!selectedUser}
          />
        </div>
        <div className="flex flex-col justify-center items-center relative">
          <span className="text-gray-500 font-semibold text-lg mb-2">-</span>
        </div>
        {/* End Date */}
        <div className="flex flex-col">
          <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
            End Date <Tooltip text={"Select both dates for custom transaction"}>
              <FiInfo
                          size={10}
                          className="cursor-pointer text-primary hover:text-secondary text-lg"
                        />
              </Tooltip>
              
          </label>
          <input
            type="date"
            value={dateRange.to || ""}
            onChange={(e) =>
              setDateRange((prev) => ({ ...prev, to: e.target.value }))
            }
            className="w-36 md:w-40 border-gray-300 rounded-lg px-3 py-2 text-gray-700 disabled:bg-gray-100 disabled:text-gray-400"
            disabled={!selectedUser}
          />
        </div>
      </div>

      {!selectedUser ? (
        <div className="flex justify-center text-center text-base text-gray-500 mt-10">
          <FaUser className="mr-2 mt-1" />
          <span>
            Select a <span className="font-semibold">User </span> to view their
            ledger.
          </span>
        </div>
      ) : loading && ledgerData.length === 0 ? (
        <div className="flex justify-center pt-10">
          <Spinner size={4} />
        </div>
      ) : ledgerData.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">
          {MISC.NO_USER_LEDGER_FOUND}
        </div>
      ) : (
        <>
          <div className="overflow-y-auto h-[52vh] md:h-[50vh] pb-4 rounded-md relative">
            <GenericTable
              tableColumns={LEDGER_COLUMNS.filter((col) => col !== "site_name")}
              tableRows={ledgerData}
              columnLabels={LEDGER_LABELS}
              renderField={renderField}
              renderMobileCard={renderMobileField}
              mobileGridColumns={2}
              rowClick={(row) => handleRowClick(row)}
            />
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </>
      )}
    </div>
  );
};

export default UserLedgerList;
